import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { School as SchoolIcon, Users, Sliders, Sparkles, Repeat } from "lucide-react";
import { Stat } from "@/pages/admin/Stat";
import { MetricsPanel } from "@/pages/admin/MetricsPanel";
import { UserConfigPanel } from "@/pages/admin/UserConfigPanel";
import { LeaderboardPanel } from "@/pages/admin/LeaderboardPanel";
import { PreventivePanel } from "@/pages/admin/PreventivePanel";
import { PortRequestsPanel } from "@/pages/admin/PortRequestsPanel";

export default function AdminDashboard() {
  const [metrics, setMetrics] = useState([]);
  const [schools, setSchools] = useState([]);
  const [users, setUsers] = useState([]);
  const [students, setStudents] = useState([]);
  const [tips, setTips] = useState([]);
  const [activities, setActivities] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [ports, setPorts] = useState([]);

  const loadAll = async () => {
    try {
      const [m, s, u, st, t, a, l, p] = await Promise.all([
        http.get("/metrics"),
        http.get("/schools"),
        http.get("/users"),
        http.get("/students"),
        http.get("/preventive"),
        http.get("/leaderboard/activities"),
        http.get("/leaderboard"),
        http.get("/accounts/port"),
      ]);
      setMetrics(m.data); setSchools(s.data); setUsers(u.data);
      setStudents(st.data); setTips(t.data); setActivities(a.data); setLeaderboard(l.data);
      setPorts(p.data);
    } catch { toast.error("Failed to load"); }
  };

  useEffect(() => { loadAll(); }, []);

  const pendingPorts = ports.filter((p) => p.status === "pending").length;

  return (
    <AppShell subtitle="Super Admin · KOI Coordinator" title="Configure & command your programs">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat label="Schools" value={schools.length} icon={<SchoolIcon className="w-4 h-4" />} accent="sky" />
        <Stat label="Students" value={students.length} icon={<Users className="w-4 h-4" />} accent="emerald" />
        <Stat label="Metrics" value={metrics.length} icon={<Sliders className="w-4 h-4" />} accent="amber" />
        <Stat label="Preventive tips" value={tips.length} icon={<Sparkles className="w-4 h-4" />} accent="violet" />
      </div>

      <Tabs defaultValue="metrics" className="w-full">
        <TabsList className="bg-white border border-slate-200 p-1 rounded-xl flex-wrap h-auto" data-testid="admin-tabs">
          <TabsTrigger value="metrics" data-testid="tab-metrics">Metrics</TabsTrigger>
          <TabsTrigger value="user-config" data-testid="tab-user-config"><Users className="w-3 h-3 mr-1" /> User Configuration</TabsTrigger>
          <TabsTrigger value="leaderboard" data-testid="tab-leaderboard">Leaderboard</TabsTrigger>
          <TabsTrigger value="preventive" data-testid="tab-preventive-admin">Preventive</TabsTrigger>
          <TabsTrigger value="ports" data-testid="tab-ports">
            <Repeat className="w-3 h-3 mr-1" /> Port Requests
            {pendingPorts > 0 && <Badge className="ml-2 bg-amber-500 hover:bg-amber-500 text-white text-[10px] px-1.5 h-4">{pendingPorts}</Badge>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="metrics" className="mt-6">
          <MetricsPanel metrics={metrics} reload={loadAll} />
        </TabsContent>

        <TabsContent value="user-config" className="mt-6">
          <UserConfigPanel students={students} users={users} schools={schools} reload={loadAll} />
        </TabsContent>

        <TabsContent value="leaderboard" className="mt-6">
          <LeaderboardPanel leaderboard={leaderboard} activities={activities} reload={loadAll} />
        </TabsContent>

        <TabsContent value="preventive" className="mt-6">
          <PreventivePanel tips={tips} reload={loadAll} />
        </TabsContent>

        <TabsContent value="ports" className="mt-6">
          <PortRequestsPanel ports={ports} reload={loadAll} />
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}
